import { getValidSpotifyUri } from 'components/ContentReporting/validation-utils';
import { getCountryCode } from 'components/ContentReporting/form-utils';
import {
  ALL_AUTHENTICATION_FIELDS_IN_PAYLOAD,
  ALL_FIELDS_IN_EMPLOYEE_PAYLOAD,
  ALL_FIELDS_IN_EXTERNAL_PAYLOAD,
  ALL_SPOTIFY_AUTHENTICATED_FIELDS_IN_PAYLOAD,
  Field,
  OptionsTeam,
} from 'components/ContentReporting/ContentPolicyReporting/content-policy-reporting-config';
import {
  ContentType,
  contentTypeFromJSON,
  EmployeeContentPolicyReportRequest,
  ExternalContentPolicyReportRequest,
  Reason,
  reasonFromJSON,
  Urgency,
  urgencyFromJSON,
} from 'proto/generated/tintin_content_policy';
import { QueryParams } from 'proto/generated/webform_query_params';
import { AssociatedContent, EntityType } from 'proto/generated/inspector_gadget';
import { formatAssociatedContentIntoEntity } from 'utils/functions';
import { assetTypeFromJSON } from 'proto/generated/spotify/catalogmanagement/entity/v1/asset';

import { Experiments } from '../types';
import { removeFullTranslationKey } from '../functions';

export const getPayloadBasedUserType = (isEmployee: boolean): Array<Field> => {
  if (isEmployee) {
    return ALL_FIELDS_IN_EMPLOYEE_PAYLOAD;
  }

  return ALL_FIELDS_IN_EXTERNAL_PAYLOAD;
};

export const getPayloadBasedOnVerificationStep = (hasVerificationStep: boolean): Array<Field> => {
  if (hasVerificationStep) {
    return ALL_AUTHENTICATION_FIELDS_IN_PAYLOAD;
  }

  return ALL_SPOTIFY_AUTHENTICATED_FIELDS_IN_PAYLOAD;
};

const getVerificationDetails = (fieldValue: (field: Field) => string) => {
  const resourceUri: string = fieldValue(Field.VERIFICATION_RESOURCE_URI);
  const code: string = fieldValue(Field.VERIFICATION_CODE);

  return {
    googleRecaptchaToken: fieldValue(Field.VERIFICATION_CAPTCHA_TOKEN),
    resourceUri,
    code,
  };
};

const getCommonReportFields = (
  fieldValue: (field: Field) => string | any,
  market: string,
  experiments?: Array<Experiments>,
  queryParams?: QueryParams,
) => {
  const productUri: string = getValidSpotifyUri(fieldValue(Field.REPORTED_CONTENT_URIS));
  const contentType: ContentType = contentTypeFromJSON(fieldValue(Field.REPORTED_CONTENT_TYPE));
  const commonAssetType = assetTypeFromJSON(fieldValue(Field.COMMON_ASSET_TYPE));
  const asset: AssociatedContent = fieldValue(Field.REPORTED_ASSET_ID);
  const [associatedEntity] = !!asset ? formatAssociatedContentIntoEntity([asset]) : [];
  const reason: Reason = reasonFromJSON(fieldValue(Field.REASON));
  const username: string = fieldValue(Field.USERNAME);
  const reporterEmail: string = fieldValue(Field.REPORTER_EMAIL);

  return {
    reporterEmail,
    reporterCountryCode: getCountryCode(market),
    productUri,
    contentType,
    commonAssetType,
    reason,
    experiments: experiments || [],
    reporterUserAgent: '',
    queryParams,
    entityType: EntityType.ENTITY_TYPE_UNKNOWN,
    ...(!!username ? { username } : {}),
    ...(!!associatedEntity ? { associatedEntity, assetId: associatedEntity.assetId } : {}),
  };
};

export const buildExternalRequest = (
  fieldValue: (field: Field) => string,
  market: string,
  experiments?: Array<Experiments>,
  queryParams?: QueryParams,
): ExternalContentPolicyReportRequest => {
  return {
    verificationDetails: getVerificationDetails(fieldValue),
    report: {
      ...getCommonReportFields(fieldValue, market, experiments, queryParams),
      skipMetadataEnrichment: false,
    },
  };
};

export const buildEmployeeRequest = (
  fieldValue: (field: Field) => string,
  market: string,
  experiments?: Array<Experiments>,
  queryParams?: QueryParams,
): EmployeeContentPolicyReportRequest => {
  const team: string =
    fieldValue(Field.TEAM) === OptionsTeam.OTHER ? fieldValue(Field.TEAM_OTHER) : fieldValue(Field.TEAM);
  const urgency: Urgency = urgencyFromJSON(removeFullTranslationKey(fieldValue(Field.URGENCY)));
  const additionalInfo: string = fieldValue(Field.ADDITIONAL_INFO);

  return {
    verificationDetails: getVerificationDetails(fieldValue),
    report: {
      ...getCommonReportFields(fieldValue, market, experiments, queryParams),
      team: team,
      urgency: urgency,
      additionalInfo: additionalInfo,
    },
  };
};
